<template>
  <section class="comparison" aria-labelledby="comparison-title">
    <div class="comparison-heading">
      <div>
        <h2 id="comparison-title" ref="heading" tabindex="-1">Compare sales</h2>
        <p>Choose two periods or ceremonies to see what changed.</p>
      </div>
      <button type="button" @click="$emit('close')">Back to overview</button>
    </div>
    <form class="comparison-form" @submit.prevent="compare(false)">
      <div class="scope-grid">
        <label
          >Compare by
          <select v-model="draft.kind" @change="changeKind">
            <option v-if="draft.intervals.length" value="elapsed">Selected interval vs preceding interval</option>
            <option value="periods">Date ranges / seasons</option>
            <option value="year">Year to date vs last year</option>
            <option value="ceremonies">Two ceremonies</option>
          </select>
        </label>
        <label
          >University
          <select v-model="universityKey">
            <option value="">
              {{
                draft.kind === "ceremonies"
                  ? "Choose a university"
                  : "All permitted universities"
              }}
            </option>
            <option
              v-for="university in universities"
              :key="university.key"
              :value="university.key"
            >
              {{ university.name }}
            </option>
          </select>
        </label>
        <label
          >Sales channel
          <select v-model="draft.channel">
            <option value="all">All channels</option>
            <option value="online">Online</option>
            <option value="pos">Point of sale</option>
          </select>
        </label>
      </div>
      <p v-if="draft.kind === 'year'" id="period-help">
        Year to date through today ({{ draft.timezone }}), compared with the same calendar dates
        last year. A leap day is matched to 28 February. These are partial
        years.
      </p>
      <p v-else-if="draft.kind === 'ceremonies'" id="period-help">
        Lifetime sales through the same cutoff. Choose the pair yourself;
        ceremonies may have had different lengths of time on sale.
      </p>
      <p v-else-if="draft.kind === 'elapsed'" id="period-help">
        The selected interval compared with the immediately preceding interval of equal elapsed time.
        Both use {{ draft.timezone }}. Calendar days can differ in length across daylight saving changes.
      </p>
      <p v-else id="period-help">
        Dates include both endpoints, in {{ draft.timezone }}. The current day may be partial. For seasons or academic years,
        enter the exact dates used by your university.
      </p>
      <div class="period-grid" aria-describedby="period-help">
        <fieldset v-for="(period, index) in draft.periods" :key="index">
          <legend>
            {{ index === 0 ? "A · Selected period" : "B · Comparison period" }}
          </legend>
          <label v-if="draft.kind === 'ceremonies'"
            >{{ index === 0 ? "Ceremony A" : "Ceremony B" }}
            <select
              :value="index === 0 ? draft.aCeremony : draft.bCeremony"
              @change="selectCeremony(index, $event.target.value)"
            >
              <option value="">Choose a ceremony</option>
              <option
                v-for="ceremony in ceremonies"
                :key="ceremony.id"
                :value="ceremony.id"
              >
                {{ ceremony.name
                }}{{
                  ceremony.datetime
                    ? " · " + ceremony.datetime.slice(0, 10)
                    : ""
                }}
              </option>
            </select>
          </label>
          <p v-else-if="draft.kind === 'elapsed'">{{ intervalLabel(draft.intervals[index], draft.timezone) }}</p>
          <div v-else class="date-grid">
            <label
              >From {{ index === 0 ? "A" : "B"
              }}<input
                v-model="period.from"
                type="date"
                required
                :readonly="draft.kind === 'year'"
                :aria-invalid="attempted && !!validationError"
                :aria-describedby="
                  attempted && validationError ? 'comparison-validation' : null
                "
            /></label>
            <label
              >To {{ index === 0 ? "A" : "B"
              }}<input
                v-model="period.to"
                type="date"
                required
                :readonly="draft.kind === 'year'"
                :aria-invalid="attempted && !!validationError"
                :aria-describedby="
                  attempted && validationError ? 'comparison-validation' : null
                "
            /></label>
          </div>
        </fieldset>
      </div>
      <label v-if="draft.kind === 'ceremonies'" class="cutoff"
        >Lifetime cutoff ({{ draft.timezone }})<input
          v-model="draft.cutoff"
          type="date"
          required
      /></label>
      <div class="form-actions">
        <label v-if="capabilities.disputes_enabled" class="checkbox"
          ><input v-model="draft.include_disputes" type="checkbox" /> Include
          dispute adjustments</label
        >
        <button type="submit" class="primary" :disabled="loading">
          {{ loading ? "Comparing…" : "Compare sales" }}
        </button>
      </div>
      <p
        v-if="attempted && validationError"
        id="comparison-validation"
        class="error"
        role="alert"
      >
        {{ validationError }}
      </p>
    </form>
    <p v-if="loading" role="status">
      {{
        exporting
          ? "Checking access and refreshing both reports for export…"
          : "Loading both reports…"
      }}
    </p>
    <p v-if="error" class="error" role="alert">{{ error }}</p>
    <section
      v-if="snapshot"
      class="comparison-results"
      aria-labelledby="comparison-results-title"
    >
      <div class="results-heading">
        <div>
          <h2 id="comparison-results-title" ref="results" tabindex="-1">
            Sales comparison
          </h2>
          <p>
            {{ snapshot.scopeLabel }} · {{ snapshot.channelLabel }} ·
            {{ snapshot.includeDisputes ? "Including" : "Excluding" }} dispute
            adjustments
          </p>
        </div>
        <button type="button" :disabled="loading" @click="compare(true)">
          Export comparison CSV
        </button>
      </div>
      <div class="period-grid coverage-grid">
        <article v-for="(report, index) in snapshot.reports" :key="index">
          <h3>{{ index === 0 ? "A" : "B" }} · {{ snapshot.labels[index] }}</h3>
          <p class="coverage">{{ coverageLabel(report) }}</p>
          <p>
            Last sync:
            {{
              (report.freshness && report.freshness.last_successful_sync) ||
              "Not available"
            }}{{ report.freshness && report.freshness.stale ? " · Stale" : "" }}
          </p>
          <p
            v-for="message in (report.coverage && report.coverage.messages) ||
            []"
            :key="message"
          >
            {{ message }}
          </p>
        </article>
      </div>
      <p class="comparison-note">
        Change is A minus B. Payments and refunds use customer currency;
        processing and net use settlement currency. Refunds and fees are negative
        deductions; credits are positive. Percentage change is (A − B) / |B|.
      </p>
      <p v-if="incomplete" class="coverage-warning" role="status">
        Known totals only. One or both reports have missing or unresolved
        history; changes are withheld. Missing history is not zero.
      </p>
      <p
        v-if="
          snapshot.reports.every((report) => !report.has_activity) &&
          !incomplete
        "
      >
        No activity was reported in either selection.
      </p>
      <SalesComparisonTable
        v-for="group in snapshot.groups"
        :key="group.title"
        :group="group"
      />
      <p class="comparison-note">
        Product quantities can exceed orders because of bundles. Payments
        reflect the original price paid; changes may reflect price, product mix
        or volume. “No baseline” means B is zero. Complete source coverage does not mean financial
        reconciliation.
      </p>
      <section
        v-if="snapshot.universities.length > 1"
        class="universities"
        aria-labelledby="comparison-universities"
      >
        <h2 id="comparison-universities">By university</h2>
        <p>
          Open a university for its totals. Choose a university and two
          ceremonies above for ceremony detail.
        </p>
        <details
          v-for="university in snapshot.universities.slice(0, universityLimit)"
          :key="university.id"
        >
          <summary>
            {{ university.name }}
          </summary>
          <SalesComparisonTable
            v-for="group in university.groups"
            :key="group.title"
            :group="group"
          />
        </details>
        <button
          v-if="snapshot.universities.length > universityLimit"
          type="button"
          @click="universityLimit += 10"
        >
          Show 10 more universities
        </button>
      </section>
    </section>
  </section>
</template>
<script>
import axios from "axios";
import SalesComparisonTable from "./SalesComparisonTable.vue";
import { localDate, intervalLabel, precedingInterval } from "../../services/salesPeriods.cjs";
import {
  yearToDate,
  previousPeriod,
  validation,
  requestParams,
  comparisonRows,
  universityComparisons,
  comparisonCsv,
} from "../../services/salesComparison.cjs";

export default {
  name: "SalesComparison",
  components: { SalesComparisonTable },
  props: {
    capabilities: { type: Object, required: true },
    initialFilters: { type: Object, required: true },
    tenantLabel: { type: Function, required: true },
  },
  data() {
    const timezone = this.initialFilters.timezone || "UTC";
    const today = localDate(new Date(), timezone);
    const initial = this.initialFilters;
    const interval = initial.mode === "activity" && initial.start_at && Date.parse(initial.end_at) > Date.parse(initial.start_at)
      ? { start_at: initial.start_at, end_at: initial.end_at } : null;
    const from = this.initialFilters.from || `${today.slice(0, 8)}01`;
    const to = this.initialFilters.to || today;
    return {
      draft: {
        kind: interval ? "elapsed" : "periods",
        timezone,
        intervals: interval ? [interval, precedingInterval(interval)] : [],
        tenant: this.initialFilters.tenant,
        university_slug: this.initialFilters.university_slug,
        channel: this.initialFilters.channel,
        include_disputes:
          this.capabilities.disputes_enabled &&
          this.initialFilters.include_disputes,
        periods: [{ from, to }, previousPeriod(from, to)],
        cutoff: today,
        aCeremony: "",
        bCeremony: "",
      },
      snapshot: null,
      error: "",
      attempted: false,
      loading: false,
      exporting: false,
      serial: 0,
      cancellation: null,
      universityLimit: 10,
    };
  },
  computed: {
    universities() {
      return (this.capabilities.scopes || [])
        .filter((scope) => scope.tenant === this.initialFilters.tenant)
        .reduce(
          (all, scope) =>
            all.concat(
              (scope.universities || []).map((university) => ({
                ...university,
                tenant: scope.tenant,
                key: `${scope.tenant}:${university.slug}`,
              }))
            ),
          []
        );
    },
    universityKey: {
      get() {
        return this.draft.university_slug
          ? `${this.draft.tenant}:${this.draft.university_slug}`
          : "";
      },
      set(key) {
        const university = this.universities.find((row) => row.key === key);
        this.draft.university_slug = university ? university.slug : "";
        this.draft.aCeremony = "";
        this.draft.bCeremony = "";
      },
    },
    ceremonies() {
      const university = this.universities.find(
        (row) => row.key === this.universityKey
      );
      return university ? university.ceremonies || [] : [];
    },
    validationError() {
      return validation(this.draft);
    },
    incomplete() {
      return (
        this.snapshot &&
        this.snapshot.reports.some(
          (report) =>
            !report.coverage ||
            report.coverage.status !== "complete" ||
            report.coverage.unknown_line_amounts ||
            report.coverage.unmapped_count
        )
      );
    },
  },
  watch: {
    draft: {
      deep: true,
      handler() {
        this.invalidate();
      },
    },
  },
  mounted() {
    this.$refs.heading.focus();
  },
  beforeDestroy() {
    this.invalidate();
  },
  methods: {
    intervalLabel,
    invalidate() {
      this.serial += 1;
      if (this.cancellation) this.cancellation.cancel();
      this.loading = false;
      this.exporting = false;
      this.snapshot = null;
      this.error = "";
    },
    selectCeremony(index, value) {
      if (index === 0) this.draft.aCeremony = value;
      else this.draft.bCeremony = value;
    },
    changeKind() {
      if (this.draft.kind === "year") this.draft.periods = yearToDate(localDate(new Date(), this.draft.timezone));
      this.attempted = false;
    },
    coverageLabel(report) {
      const status = report.coverage && report.coverage.status;
      return status === "complete"
        ? "Complete source coverage"
        : status === "not_synced"
        ? "History not synced · totals unavailable"
        : "Incomplete source coverage · known totals only";
    },
    async compare(exporting) {
      this.attempted = true;
      if (this.validationError || this.loading) return;
      this.error = "";
      this.loading = true;
      this.exporting = exporting;
      const serial = ++this.serial;
      this.cancellation = axios.CancelToken.source();
      const cancelToken = this.cancellation.token;
      const draft = JSON.parse(JSON.stringify(this.draft));
      const params = requestParams(draft);
      const labels =
        draft.kind === "ceremonies"
          ? [draft.aCeremony, draft.bCeremony].map(
              (id) =>
                `${
                  this.ceremonies.find((row) => row.id === id).name
                } · lifetime through ${draft.cutoff} ${draft.timezone}`
            )
          : draft.kind === "elapsed"
          ? draft.intervals.map((p) => `${intervalLabel(p, draft.timezone)} · ${draft.timezone}`)
          : draft.periods.map(
              (period) =>
                `${period.from} to ${period.to} ${draft.timezone}${
                  draft.kind === "year" ? " · year to date" : ""
                }`
            );
      const university = this.universities.find(
        (row) => row.key === this.universityKey
      );
      try {
        // Each request is independently authorized by the existing aggregate API.
        // Export repeats both requests so a revoked grant cannot use cached totals.
        const reports = await Promise.all(
          params.map(async (query, index) => {
            try {
              return (
                await axios.get("/analytics/orders/v2", {
                  params: query,
                  cancelToken,
                })
              ).data;
            } catch (error) {
              error.comparisonSide = index ? "B" : "A";
              throw error;
            }
          })
        );
        if (serial !== this.serial) return;
        const snapshot = {
          reports,
          params,
          labels,
          includeDisputes: draft.include_disputes,
          scopeLabel: university
            ? `${university.name} · ${this.tenantLabel(university.tenant)}`
            : this.tenantLabel(draft.tenant),
          channelLabel:
            draft.channel === "all"
              ? "All channels"
              : draft.channel === "pos"
              ? "Point of sale"
              : "Online",
          groups: comparisonRows(...reports, draft.include_disputes),
          universities: universityComparisons(
            ...reports,
            draft.include_disputes
          ),
        };
        if (exporting) {
          const csv = comparisonCsv(snapshot);
          const url = URL.createObjectURL(
            new Blob(["\ufeff", csv], { type: "text/csv;charset=utf-8;" })
          );
          const link = document.createElement("a");
          link.href = url;
          link.download = "sales-comparison.csv";
          document.body.appendChild(link);
          link.click();
          link.remove();
          setTimeout(() => URL.revokeObjectURL(url), 1000);
        }
        this.snapshot = snapshot;
        this.universityLimit = 10;
        if (!exporting)
          this.$nextTick(() => {
            if (this.$refs.results) this.$refs.results.focus();
          });
      } catch (error) {
        if (serial !== this.serial || axios.isCancel(error)) return;
        this.cancellation.cancel();
        this.snapshot = null;
        this.error =
          error.response && error.response.status === 403
            ? `Access to report ${error.comparisonSide} is no longer available. Neither comparison nor export is available. Check your access and try again.`
            : error.comparisonSide
            ? `Could not load report ${error.comparisonSide}. Please try comparing again.`
            : error.message;
      } finally {
        if (serial === this.serial) {
          this.loading = false;
          this.exporting = false;
        }
      }
    },
  },
};
</script>
<style scoped>
.comparison {
  color: #17263d;
}
h2 {
  margin: 0 0 6px;
  font-size: 21px;
  font-weight: 700;
}
h3 {
  margin: 0 0 10px;
  font-size: 16px;
  font-weight: 700;
  overflow-wrap: anywhere;
}
p {
  font-size: 13px;
  line-height: 1.6;
  color: #53657a;
  margin: 6px 0 16px;
}
.comparison-heading,
.results-heading {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 18px;
  margin: 24px 0 18px;
}
button {
  border: 1px solid #cbd6e3;
  background: white;
  color: #365470;
  border-radius: 7px;
  padding: 10px 15px;
  font-size: 13px;
  font-weight: 650;
  min-height: 44px;
  cursor: pointer;
}
button:disabled {
  opacity: 0.65;
  cursor: wait;
}
button.primary {
  background: #215e9a;
  border-color: #215e9a;
  color: white;
}
button:hover:not(:disabled) {
  background: #eaf2fa;
}
button.primary:hover:not(:disabled) {
  background: #174b7d;
}
button:focus-visible,
input:focus-visible,
select:focus-visible,
summary:focus-visible {
  outline: 2px solid #1768aa;
  outline-offset: 3px;
}
h2:focus {
  outline: none;
}
.comparison-form {
  background: white;
  border: 1px solid #dce4ed;
  border-radius: 14px;
  padding: 22px;
  box-shadow: 0 3px 15px #19385808;
}
.scope-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 16px;
  margin-bottom: 18px;
}
label {
  display: flex;
  flex-direction: column;
  gap: 7px;
  font-size: 13px;
  font-weight: 650;
  color: #36506b;
  min-width: 0;
}
input:not([type="checkbox"]),
select {
  width: 100%;
  min-width: 0;
  min-height: 44px;
  padding: 9px 10px;
  border: 1px solid #9eafc1;
  border-radius: 6px;
  background: white;
  color: #17263d;
  font-size: 14px;
  box-sizing: border-box;
}
input[readonly] {
  background: #f3f6fa;
}
.period-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 20px;
}
fieldset {
  min-width: 0;
  padding: 14px;
  border: 1px solid #dce4ed;
  border-radius: 9px;
}
legend {
  padding: 0 6px;
  font-size: 14px;
  font-weight: 700;
  color: #365470;
}
.date-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
}
.cutoff {
  max-width: 260px;
  margin-top: 16px;
}
.form-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 22px;
  margin-top: 20px;
}
.checkbox {
  flex-direction: row;
  align-items: center;
  min-height: 44px;
  font-weight: 500;
}
.checkbox input {
  width: 18px;
  height: 18px;
  accent-color: #215e9a;
}
.error {
  padding: 16px;
  background: #fff1f0;
  color: #922b2b;
  border-radius: 8px;
}
.coverage-grid article {
  padding: 18px;
  border-radius: 10px;
  background: #f1f5fa;
}
.coverage-grid p {
  margin-bottom: 5px;
  overflow-wrap: anywhere;
}
.coverage {
  font-weight: 650;
  color: #365470;
}
.comparison-note {
  margin-top: 18px;
}
.coverage-warning {
  background: #fff4db;
  padding: 15px;
  border-radius: 8px;
  color: #725219;
}
.universities {
  margin-top: 28px;
}
details {
  border-bottom: 1px solid #dce4ed;
  padding: 14px 0;
}
summary {
  cursor: pointer;
  min-height: 44px;
  font-weight: 650;
  overflow-wrap: anywhere;
}
summary span {
  display: block;
  font-size: 12px;
  color: #53657a;
  font-weight: 400;
  padding-left: 16px;
}
@media (max-width: 900px) {
  .scope-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
@media (max-width: 600px) {
  .comparison-heading,
  .results-heading {
    flex-direction: column;
    gap: 8px;
  }
  .scope-grid,
  .period-grid {
    grid-template-columns: minmax(0, 1fr);
    gap: 16px;
  }
  .comparison-form {
    padding: 16px;
  }
  .date-grid {
    grid-template-columns: minmax(0, 1fr);
  }
  .form-actions {
    align-items: stretch;
    flex-direction: column;
    gap: 10px;
  }
}
</style>
